import type { Level, ModelRef, Opening, Vec2 } from '../core/hp/types';
import { addWall, MIN_WALL, projectPoint, validateLevel, type EditResult } from '../core/geometry/edit';
import { snapOpeningOffset, type SnapGuide } from '../core/geometry/snapping';
import { mirrorOpening } from '../core/geometry/openingTwins';
import { t } from '../i18n/I18n';

export type OpeningKind = 'door' | 'window' | 'arch';
/** What a new opening of each kind measures. */
export const OPENING_SPECS = { door: { width: .9, height: 2.1, sill: 0 }, window: { width: 1.2, height: 1.4, sill: .9 }, arch: { width: 1.4, height: 2.4, sill: 0 } } as const;
/** A catalog door, window or arch: its model and its size instead of the editor's own drawn leaf (`catalogPlacement.ts`). */
export interface CatalogOpening { modelRef: ModelRef; caption: string; width: number; height: number; sill: number }

/**
 * A new opening of `kind` on the wall `wallId`: centred at `offset`, or else on the free spot nearest the
 * wall's middle. Between two rooms of a project from the app it also gets its copy on the other room's
 * copy of the wall (`mirrorOpening`), listed before it, so the new opening is the last in the list.
 * With `model`, a catalog's: its size, a sill lowered as far as the wall needs, and `offset` only where it
 * is wanted — the free spot nearest it is taken when something already stands there.
 */
export function insertOpening(level: Level, wallId: string, kind: OpeningKind, offset?: number, model?: CatalogOpening): EditResult {
  const wall = level.walls.find(w => w.id === wallId);
  if (!wall) return { error: t('#GeometryWallNotFound') };
  const spec = model ? { width: model.width, height: model.height, sill: Math.max(0, Math.min(model.sill, wall.height - model.height)) } : OPENING_SPECS[kind];
  if (spec.width > wall.length - .04 || spec.height + spec.sill > wall.height) return { error: t('#GeometryOpeningDoesNotFit') };
  const wanted = offset ?? wall.length / 2;
  const candidates = offset === undefined || model ? [wanted, spec.width / 2 + .02, wall.length - spec.width / 2 - .02] : [offset];
  if (offset === undefined || model) {
    for (const other of level.openings.filter(o => o.wallId === wallId)) {
      const gap = (other.width + spec.width) / 2 + .02;
      candidates.push(other.offset - gap, other.offset + gap);
    }
    candidates.sort((a, b) => Math.abs(a - wanted) - Math.abs(b - wanted));
  }
  for (const candidate of candidates) {
    // A wanted spot a little past the end slides in; another opening's neighbour that does not fit is skipped.
    const at = model ? Math.max(spec.width / 2 + .02, Math.min(wall.length - spec.width / 2 - .02, candidate)) : candidate;
    if (!Number.isFinite(at) || at < spec.width / 2 + .02 || at > wall.length - spec.width / 2 - .02) continue;
    const opening: Opening = model
      ? { id: `o_${crypto.randomUUID()}`, wallId, modelRef: { ...model.modelRef }, offset: Math.round(at * 1e4) / 1e4, ...spec, flipped: false, caption: model.caption, kind }
      : { id: `o_${crypto.randomUUID()}`, wallId, modelRef: { kind: 'none', id: '', raw: '' },
        offset: Math.round(at * 1e4) / 1e4, ...spec, flipped: false, caption: kind, kind,
        ...(kind !== 'arch' ? { design: { style: kind === 'door' ? 'solid' as const : 'double' as const } } : {}) };
    const next = { ...level, openings: [...level.openings, opening] };
    if (!validateLevel(next, level)) return { level: mirrorOpening(next, opening.id) };
  }
  return { error: t('#PlanConstructionNoFreeSpot') };
}

/** The wall under the point takes the opening; with `snap`, the opening also seeks the wall's middle or an even spacing. */
export function projectOpening(level: Level, point: Vec2, kind: OpeningKind, tolerance: number,
  snap?: { scale: number; disabled?: boolean }): EditResult & { guides?: SnapGuide[] } {
  let nearest: { id: string; offset: number; distance: number } | undefined;
  for (const wall of level.walls) {
    const projected = projectPoint(point, wall.start, wall.end);
    const distance = Math.hypot(point.x - projected.point.x, point.z - projected.point.z);
    if (distance <= tolerance && (!nearest || distance < nearest.distance)) nearest = { id: wall.id, offset: projected.t * wall.length, distance };
  }
  if (!nearest) return { error: t('#PlanConstructionMoveToFreeSpot') };
  const snapped = snap ? snapOpeningOffset(level, nearest.id, OPENING_SPECS[kind].width, nearest.offset, snap) : { offset: nearest.offset, guides: [] };
  return { ...insertOpening(level, nearest.id, kind, snapped.offset), guides: snapped.guides };
}

export function rectangularRoom(level: Level, start: Vec2, end: Vec2, thickness?: number): EditResult {
  if (![start.x, start.z, end.x, end.z].every(Number.isFinite) || Math.abs(end.x - start.x) < MIN_WALL || Math.abs(end.z - start.z) < MIN_WALL)
    return { error: t('#PlanConstructionRoomTooSmall') };
  const x0 = Math.min(start.x, end.x), x1 = Math.max(start.x, end.x), z0 = Math.min(start.z, end.z), z1 = Math.max(start.z, end.z);
  const points = [{ x: x0, z: z0 }, { x: x1, z: z0 }, { x: x1, z: z1 }, { x: x0, z: z1 }];
  let next = level;
  for (let i = 0; i < points.length; i++) {
    const result = addWall(next, points[i], points[(i + 1) % points.length], thickness, { reuseCovered: true });
    if (!result.level) return result;
    next = result.level;
  }
  return { level: next };
}
